import { gql, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import type { Organization } from '@interfaces/organization';
import { MenuItem } from '@mui/material';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { UserContext } from '@providers/userContextProvider';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useLocalization } from '../../../hooks/useLocalization';

const FETCH_ORGANIZATIONS = gql`
  query fetchOrganizations($input: FetchOrganizationsInput!) {
    fetchOrganizations(input: $input) {
      organizations {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
      }
      totalCount
    }
  }
`;
type OrganizationSelectProp = {
  label: string;
  onChange: (id: string) => void;
  value: string | undefined;
  sx?: any;
  size?: 'small' | 'medium';
  showValue?: boolean;
  filter?: any;
  onBlur?: any;
  error?: any;
  testId?: string;
  organizationId?: string;
};
export const OrganizationSelect = ({
  label,
  onChange,
  value,
  sx = {},
  size,
  filter,
  onBlur,
  error,
  testId,
  organizationId,
}: OrganizationSelectProp) => {
  const { translateBackendString } = useLocalization();
  const { activeOrganization } = useContext(UserContext);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  const { data } = useQuery(FETCH_ORGANIZATIONS, {
    variables: {
      input: {
        filter: {
          ...filter,
          childrenFor: organizationId ?? activeOrganization.id,
        },
        pagination: {
          perPage: 1000,
        },
      },
      fetchPolicy: 'cache-and-network',
    },
  });

  useEffect(() => {
    if (
      data?.fetchOrganizations?.organizations &&
      data?.fetchOrganizations?.organizations.length > 0
    ) {
      setOrganizations(
        [...(data?.fetchOrganizations?.organizations ?? [])].sort(
          (a: any, b: any) =>
            translateBackendString(a.translatedName).localeCompare(
              translateBackendString(b.translatedName),
            ),
        ),
      );
    } else {
      setOrganizations([
        {
          id: activeOrganization.id ?? '',
          translatedName:
            activeOrganization.translatedName ?? getLanguageDefaults(),
        },
      ]);
    }
  }, [data, activeOrganization]);

  return (
    <SelectField
      onChange={(e: any) => onChange(e.target.value)}
      label={label}
      fullWidth
      sx={sx}
      size={size}
      value={value ?? ''}
      onBlur={onBlur}
      error={error}
      testId={testId ?? 'organization-select'}
    >
      {organizations?.map((organization: Organization) => (
        <MenuItem
          data-testId={
            testId
              ? `${testId}-${kebabCase(translateBackendString(organization.translatedName))}`
              : `organization-select-${kebabCase(translateBackendString(organization.translatedName))}`
          }
          key={organization.id}
          value={organization.id}
        >
          {translateBackendString(organization.translatedName)}
        </MenuItem>
      )) ?? []}
    </SelectField>
  );
};
